import { memo } from 'react';
import { Navigate } from 'react-router-dom';
import { StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Lock } from 'lucide-react';

import { UNAUTHORIZED_MODES } from '../../constants/permissionMode';
import { usePermission, usePermissions } from '../../hooks/usePermission';

/**
 * Route guard. Allowed: children. Loading: nothing (no flash). Denied: `hide` redirects to the
 * home of an accessible area (a path the host supplies), `lock` shows the no-access state in place.
 *
 * @param {object} props
 * @param {string} props.permission A value of `PERMISSIONS`.
 * @param {'hide'|'lock'} [props.mode] Per-call override of the default mode.
 */
const RequirePermission = ({ permission, mode: modeOverride, children }) => {
  const { t } = useTranslation('people-access');
  const { noAccessRedirectPath } = usePermissions();
  const { allowed, loading, mode } = usePermission(permission, { mode: modeOverride });

  if (loading) return null;
  if (allowed) return children;

  if (mode === UNAUTHORIZED_MODES.LOCK) {
    return (
      // A div, not PageContainer: the layout already renders the page's <main>.
      <div className="py-10">
        <StatusMessage icon={Lock} title={t('guard.noAccessTitle')} subtitle={t('guard.locked')} />
      </div>
    );
  }

  return <Navigate to={noAccessRedirectPath} replace />;
};

export default memo(RequirePermission);
